import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../config/firebase";
import Logger from "utils/logger";

class RemoteConfigService {
  constructor() {
    this.isInitialized = false;
    this.lastFetchTime = null;
    this.cache = new Map();
  }

  /**
   * Initialize Remote Config and fetch values
   */
  async initialize() {
    try {
      Logger.debug("RemoteConfig: Initializing...");

      // Fetch and activate remote config
      await fetchAndActivate(remoteConfig);
      this.isInitialized = true;
      this.lastFetchTime = Date.now();

      Logger.debug("RemoteConfig: Initialized successfully");
      return true;
    } catch (error) {
      Logger.error("RemoteConfig: Error initializing:", error);
      return false;
    }
  }

  /**
   * Get a boolean value from remote config
   */
  getBoolean(key) {
    try {
      const value = getValue(remoteConfig, key);
      const booleanValue = value.asBoolean();
      return booleanValue;
    } catch (error) {
      Logger.error(
        `RemoteConfig: Error getting boolean value for ${key}:`,
        error
      );
      Logger.debug(
        `RemoteConfig: Falling back to default value:`,
        remoteConfig.defaultConfig[key]
      );
      // Return default value if remote config fails
      return remoteConfig.defaultConfig[key] || false;
    }
  }

  /**
   * Get a string value from remote config
   */
  getString(key) {
    try {
      const value = getValue(remoteConfig, key);
      return value.asString();
    } catch (error) {
      Logger.error(
        `RemoteConfig: Error getting string value for ${key}:`,
        error
      );
      // Return default value if remote config fails
      return remoteConfig.defaultConfig[key] || "";
    }
  }

  /**
   * Get a number value from remote config
   */
  getNumber(key) {
    try {
      Logger.debug(`RemoteConfig: Getting number value for key: ${key}`);
      const value = getValue(remoteConfig, key);
      const numberValue = value.asNumber();
      Logger.debug(`RemoteConfig: Retrieved value for ${key}:`, numberValue);
      return numberValue;
    } catch (error) {
      Logger.error(
        `RemoteConfig: Error getting number value for ${key}:`,
        error
      );
      Logger.debug(
        `RemoteConfig: Falling back to default value for ${key}:`,
        remoteConfig.defaultConfig[key]
      );
      // Return default value if remote config fails
      return remoteConfig.defaultConfig[key] || 0;
    }
  }

  /**
   * Check if gatekeepers should be enforced
   */
  get shouldEnforceGatekeepers() {
    try {
      const value = this.getBoolean("gatekeep");
      return value;
    } catch (error) {
      Logger.error("RemoteConfig: Error in shouldEnforceGatekeepers:", error);
      return true; // Default to enforcing gatekeepers on error
    }
  }

  /**
   * Get the latest app version from remote config
   */
  get latestVersion() {
    try {
      Logger.debug(
        "RemoteConfig: Fetching latest_version from remote config..."
      );
      const value = this.getNumber("latest_version");
      Logger.debug("RemoteConfig: Latest app version:", value);

      // Also log the default config value for comparison
      Logger.debug(
        "RemoteConfig: Default config latest_version:",
        remoteConfig.defaultConfig.latest_version
      );

      return value;
    } catch (error) {
      Logger.error("RemoteConfig: Error in latestVersion:", error);
      Logger.debug(
        "RemoteConfig: Falling back to default value:",
        remoteConfig.defaultConfig.latest_version
      );
      return remoteConfig.defaultConfig.latest_version;
    }
  }

  /**
   * Force refresh of remote config values
   */
  async refresh() {
    try {
      Logger.debug("RemoteConfig: Refreshing...");
      await fetchAndActivate(remoteConfig);
      this.lastFetchTime = Date.now();
      Logger.debug("RemoteConfig: Refreshed successfully");
      return true;
    } catch (error) {
      Logger.error("RemoteConfig: Error refreshing:", error);
      return false;
    }
  }

  /**
   * Get cache status
   */
  get cacheStatus() {
    return {
      isInitialized: this.isInitialized,
      lastFetchTime: this.lastFetchTime,
      isStale: this.lastFetchTime
        ? Date.now() - this.lastFetchTime > 12 * 60 * 60 * 1000
        : true, // 12 hours
    };
  }

  /**
   * Debug method to show all available remote config values
   */
  debugRemoteConfig() {
    try {
      Logger.debug("RemoteConfig: Debug - All available keys:");
      Logger.debug("RemoteConfig: Default config:", remoteConfig.defaultConfig);

      // Try to get the latest_version value directly
      const latestVersionValue = getValue(remoteConfig, "latest_version");
      Logger.debug(
        "RemoteConfig: Raw latest_version value:",
        latestVersionValue
      );
      Logger.debug(
        "RemoteConfig: latest_version asString:",
        latestVersionValue.asString()
      );
      Logger.debug(
        "RemoteConfig: latest_version asNumber:",
        latestVersionValue.asNumber()
      );

      return {
        defaultConfig: remoteConfig.defaultConfig,
        latestVersionRaw: latestVersionValue,
        latestVersionString: latestVersionValue.asString(),
        latestVersionNumber: latestVersionValue.asNumber(),
      };
    } catch (error) {
      Logger.error("RemoteConfig: Error in debugRemoteConfig:", error);
      return { error: error.message };
    }
  }
}

// Export singleton instance
const remoteConfigService = new RemoteConfigService();
export default remoteConfigService;
